import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Info, Sparkles, Smartphone } from "lucide-react";

export function AppFeatureShowcase({
  children,
  features = [],
  title = "App Funkciók",
  accentColor = "#FBD556",
}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFeature = activeIndex !== null ? features[activeIndex] : null;

  const handleHotspotClick = useCallback((index) => {
    setActiveIndex(index);
    setIsExpanded(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setTimeout(() => setActiveIndex(null), 200);
  }, []);

  const handleNext = useCallback(() => {
    if (activeIndex < features.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  }, [activeIndex, features.length]);

  const handlePrev = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  }, [activeIndex]);

  return (
    <div className="relative" style={{ width: "100%", height: "100%" }}>
      {children}

      {features.map((feature, index) => (
        <HotspotButton
          key={feature.id || index}
          feature={feature}
          index={index}
          isActive={activeIndex === index}
          totalCount={features.length}
          onClick={() => handleHotspotClick(index)}
        />
      ))}

      {isExpanded && activeFeature &&
        createPortal(
          <FeatureCard
            feature={activeFeature}
            index={activeIndex}
            totalCount={features.length}
            accentColor={accentColor}
            onClose={handleClose}
            onNext={handleNext}
            onPrev={handlePrev}
          />,
          document.body
        )}
    </div>
  );
}

function HotspotButton({ feature, index, isActive, totalCount, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  const pulseAnimation = `
    @keyframes pulse-ring {
      0% { transform: scale(1); opacity: 0.8; }
      50% { transform: scale(1.3); opacity: 0; }
      100% { transform: scale(1); opacity: 0; }
    }
    @keyframes pulse-dot {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }
  `;

  return (
    <>
      <style>{pulseAnimation}</style>
      <button
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="absolute transition-all duration-200"
        style={{
          left: `${feature.position.x}%`,
          top: `${feature.position.y}%`,
          transform: "translate(-50%, -50%)",
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "2.5px solid #242220",
          background: isActive ? "rgba(251, 213, 86, 0.75)" : "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(2px)",
          boxShadow: "0 3px 12px rgba(36, 34, 32, 0.25), inset 0 0 0 1px rgba(255,255,255,0.6)",
          cursor: "pointer",
          zIndex: isActive ? 30 : 20,
          animation: isActive ? "pulse-dot 2s ease-in-out infinite" : "none",
        }}
        aria-label={`Feature ${index + 1}: ${feature.title}`}
      >
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background: "#FBD556",
            animation: "pulse-ring 2s ease-out infinite",
            zIndex: -1,
          }}
        />
        <span
          className="flex items-center justify-center w-full h-full text-xs font-bold"
          style={{ color: "#242220" }}
        >
          {index + 1}
        </span>
      </button>

      {(isHovered || isActive) && (
        <div
          className="absolute px-3 py-2 text-xs font-bold whitespace-nowrap pointer-events-none transition-opacity duration-200"
          style={{
            left: `${feature.position.x}%`,
            top: `calc(${feature.position.y}% - 45px)`,
            transform: "translateX(-50%)",
            background: "#242220",
            color: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 40,
            opacity: isHovered || isActive ? 1 : 0,
          }}
        >
          {feature.title}
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              bottom: -6,
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #242220",
            }}
          />
        </div>
      )}
    </>
  );
}

function FeatureCard({
  feature,
  index,
  totalCount,
  accentColor,
  onClose,
  onNext,
  onPrev,
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        background: "rgba(36, 34, 32, 0.85)",
        backdropFilter: "blur(8px)",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="relative flex overflow-hidden"
        style={{
          background: "#fff",
          borderRadius: "28px",
          border: "3px solid #242220",
          boxShadow: "0 20px 60px rgba(36, 34, 32, 0.5)",
          width: "95vw",
          maxWidth: 1200,
          height: "90vh",
          animation: "slideUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>

        {/* Left Column - Image */}
        <div
          className="flex-1 flex items-center justify-center p-8"
          style={{
            background: "#FDF5EB",
            borderRight: "3px solid #242220",
          }}
        >
          {feature.image ? (
            <img
              src={feature.image}
              alt={feature.title}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                borderRadius: "20px",
                border: "2px solid #242220",
                boxShadow: "4px 4px 0 #242220",
                objectFit: "contain",
              }}
            />
          ) : (
            <div
              className="flex flex-col items-center justify-center"
              style={{
                width: "100%",
                height: "100%",
                background: "#fff",
                borderRadius: "20px",
                border: "2px solid #242220",
              }}
            >
              <Smartphone size={80} strokeWidth={1.5} color="#242220" style={{ opacity: 0.3 }} />
              <p className="text-sm font-bold mt-4" style={{ color: "#242220", opacity: 0.4 }}>
                Nincs kép
              </p>
            </div>
          )}
        </div>

        {/* Right Column - Content */}
        <div className="flex flex-col" style={{ width: 380, minWidth: 380 }}>
          {/* Header */}
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ background: accentColor, borderBottom: "3px solid #242220" }}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={20} strokeWidth={2.5} color="#242220" />
              <span className="text-base font-bold" style={{ color: "#242220" }}>
                {feature.title}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full transition-colors hover:bg-white/30"
              style={{ color: "#242220" }}
            >
              <X size={22} strokeWidth={2.5} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 flex flex-col overflow-y-auto">
            <div
              className="prose prose-sm max-w-none text-lg leading-relaxed"
              style={{ color: "#242220", opacity: 0.9, fontFamily: "Quicksand, sans-serif" }}
              dangerouslySetInnerHTML={{ __html: feature.description || "<p style='opacity:0.4'>Nincs leírás...</p>" }}
            />

            {feature.highlight && (
              <div
                className="mt-6 px-4 py-3 text-sm font-bold flex-shrink-0"
                style={{
                  background: "#A1E1B9",
                  borderRadius: "14px",
                  border: "2px solid #242220",
                  color: "#242220",
                  boxShadow: "3px 3px 0 #242220",
                }}
              >
                {feature.highlight}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ borderTop: "2px solid #e0e0e0" }}
          >
            {totalCount > 1 ? (
              <>
                <button
                  onClick={onPrev}
                  disabled={index === 0}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-full transition-all disabled:opacity-30"
                  style={{
                    background: index === 0 ? "transparent" : "#FDF5EB",
                    color: "#242220",
                    border: "2px solid #242220",
                    cursor: index === 0 ? "not-allowed" : "pointer",
                    boxShadow: index === 0 ? "none" : "2px 2px 0 #242220",
                  }}
                >
                  <ChevronLeft size={16} strokeWidth={2.5} />
                  Előző
                </button>

                <span className="text-sm font-bold" style={{ color: "#242220", opacity: 0.5 }}>
                  {index + 1} / {totalCount}
                </span>

                <button
                  onClick={onNext}
                  disabled={index === totalCount - 1}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-full transition-all disabled:opacity-30"
                  style={{
                    background: index === totalCount - 1 ? "transparent" : "#FDF5EB",
                    color: "#242220",
                    border: "2px solid #242220",
                    cursor: index === totalCount - 1 ? "not-allowed" : "pointer",
                    boxShadow: index === totalCount - 1 ? "none" : "2px 2px 0 #242220",
                  }}
                >
                  Következő
                  <ChevronRight size={16} strokeWidth={2.5} />
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="w-full py-3 text-sm font-bold rounded-full transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{
                  background: accentColor,
                  color: "#242220",
                  border: "2px solid #242220",
                  boxShadow: "3px 3px 0 #242220",
                }}
              >
                Értem!
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeatureCallout({ icon: Icon, title, children, variant = "default" }) {
  const variants = {
    default: { bg: "#FDF5EB", border: "#242220" },
    highlight: { bg: "#FBD556", border: "#242220" },
    success: { bg: "#A1E1B9", border: "#242220" },
    tip: { bg: "#FF9456", border: "#242220" },
  };

  const style = variants[variant] || variants.default;

  return (
    <div
      className="p-4 my-4"
      style={{
        background: style.bg,
        borderRadius: "16px",
        border: `1.5px solid ${style.border}`,
        boxShadow: "3px 3px 0 #242220",
      }}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
            style={{
              background: "#fff",
              borderRadius: "10px",
              border: "1.5px solid #242220",
            }}
          >
            <Icon size={20} strokeWidth={2.5} color="#242220" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm font-bold mb-1" style={{ color: "#242220" }}>
            {title}
          </p>
          <div className="text-sm" style={{ color: "#242220", opacity: 0.85, lineHeight: 1.6 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
